import React, { useMemo, useRef, useState } from "react";

import {
  ArrowDownUp,
  Clapperboard,
  Edit,
  GripVertical,
  Play,
  Plus,
  RotateCcw,
  Save,
  SearchX,
  Trash2,
} from "lucide-react";
import { createPortal } from "react-dom";

import EmptyState from "./EmptyState";
import FilterChips from "./FilterChips";
import ItemActionsMenu from "./ItemActionsMenu";
import LayoutToggle, { VideoLayout } from "./LayoutToggle";
import LibraryVideoFormModal from "./LibraryVideoFormModal";
import ReorderControls from "./ReorderControls";
import { runSmoothReorder, useReorderHighlight } from "./reorderMotion";
import { moveItemInArray, type ReorderDirection } from "./reorderUtils";
import SortableLibraryGrid from "./SortableLibraryGrid";
import useMagneticSections from "./useMagneticSections";
import { getVideoRoleKey, VIDEO_ROLE_ACCORDIONS } from "./videoOrderUtils";
import VideoPreviewModal from "./VideoPreviewModal";
import WorkspaceSearch from "./WorkspaceSearch";
import Button from "../../Components/Button";
import TruncatedTooltip from "../../Components/TruncatedTooltip";
import DeleteModal from "../../Container/DeleteModal";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import {
  removeLibraryVideo,
  reorderLibraryVideos,
  LibraryVideo,
  VideoTopic,
} from "../../Store/Reducers/VideoSlice";
import { UNCATEGORIZED_TOPIC_KEY } from "../../Utils/Helper/videoTopics";
import { getYouTubeThumbnailUrl } from "../../Utils/Helper/videoUrl";

const ALL_TOPICS_KEY = "all";

export const getRoleLabels = (roles: string[]): string[] => {
  const roleKeys = new Set(roles.map(role => getVideoRoleKey(role)));
  return VIDEO_ROLE_ACCORDIONS.filter(role => roleKeys.has(role.value)).map(
    role => role.label
  );
};

type EditSection = {
  key: string;
  label: string;
  topicId: string | null;
  videos: LibraryVideo[];
};

const bySortOrder = (a: LibraryVideo, b: LibraryVideo) =>
  (a.sort_order || 0) - (b.sort_order || 0) ||
  (a.title || "").localeCompare(b.title || "");

const sectionIds = (sections: EditSection[]) =>
  sections.flatMap(section => section.videos.map(video => video.id)).join("|");

interface LibraryPanelProps {
  actionsSlot: HTMLElement | null;
}

const LibraryPanel: React.FC<LibraryPanelProps> = ({ actionsSlot }) => {
  const dispatch = useAppDispatch();
  const {
    libraryVideos,
    topics,
    libraryLoading: loading,
    librarySaving: saving,
  } = useAppSelector(state => state.video);

  const [search, setSearch] = useState("");
  const [topicFilter, setTopicFilter] = useState(ALL_TOPICS_KEY);
  const [formState, setFormState] = useState<{
    open: boolean;
    video: LibraryVideo | null;
  }>({ open: false, video: null });
  const [layout, setLayout] = useState<VideoLayout>("cards");
  const [previewVideo, setPreviewVideo] = useState<LibraryVideo | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<LibraryVideo | null>(null);
  const [editSections, setEditSections] = useState<EditSection[] | null>(null);
  const [orderSaving, setOrderSaving] = useState(false);

  const canvasRef = useRef<HTMLDivElement>(null);
  const listReorderRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const { highlightId, flash, isHighlighted } = useReorderHighlight();
  const [scrollRequestId, setScrollRequestId] = useState(0);
  const isEditing = editSections !== null;
  // Left topic nav + stacked sections; click/scroll keep the active topic in sync.
  const isMagnetic = true;
  const query = search.trim().toLowerCase();

  const filterTabs = useMemo(() => {
    const uncategorizedCount = libraryVideos.filter(
      video => !video.topic_id
    ).length;
    return [
      { id: ALL_TOPICS_KEY, label: "All", count: libraryVideos.length },
      ...(topics as VideoTopic[]).map(topic => ({
        id: topic.id,
        label: topic.label,
        count: topic.video_count,
      })),
      ...(uncategorizedCount > 0
        ? [
            {
              id: UNCATEGORIZED_TOPIC_KEY,
              label: "Uncategorized",
              count: uncategorizedCount,
            },
          ]
        : []),
    ];
  }, [libraryVideos, topics]);

  const searchedVideos = useMemo(
    () =>
      libraryVideos.filter(
        video =>
          !query ||
          (video.title || "").toLowerCase().includes(query) ||
          (video.description || "").toLowerCase().includes(query)
      ),
    [libraryVideos, query]
  );

  const sections = useMemo(
    () =>
      filterTabs
        .filter(tab => tab.id !== ALL_TOPICS_KEY)
        .map(tab => ({
          key: tab.id,
          label: tab.label,
          videos: searchedVideos
            .filter(video =>
              tab.id === UNCATEGORIZED_TOPIC_KEY
                ? !video.topic_id
                : video.topic_id === tab.id
            )
            .sort(bySortOrder),
        }))
        .filter(section => !query || section.videos.length > 0),
    [filterTabs, searchedVideos, query]
  );

  // Multi-section (magnetic) browse: hide zero-count topics unless that chip is selected.
  const canvasSections = useMemo(
    () =>
      sections.filter(
        section =>
          section.videos.length > 0 ||
          (isMagnetic && section.key === topicFilter)
      ),
    [sections, isMagnetic, topicFilter]
  );

  const sectionKeys = useMemo(
    () =>
      isEditing
        ? (editSections || []).map(section => section.key)
        : canvasSections.map(section => section.key),
    [isEditing, editSections, canvasSections]
  );

  const savedEditSections = useMemo((): EditSection[] => {
    const topicSections = (topics as VideoTopic[]).map(topic => ({
      key: topic.id,
      label: topic.label,
      topicId: topic.id,
      videos: libraryVideos
        .filter(video => video.topic_id === topic.id)
        .sort(bySortOrder),
    }));
    const uncategorized = libraryVideos
      .filter(video => !video.topic_id)
      .sort(bySortOrder);
    return [
      ...topicSections,
      ...(uncategorized.length > 0
        ? [
            {
              key: UNCATEGORIZED_TOPIC_KEY,
              label: "Uncategorized",
              topicId: null as string | null,
              videos: uncategorized,
            },
          ]
        : []),
    ].filter(section => section.videos.length > 0);
  }, [libraryVideos, topics]);

  const hasOrderChanges =
    isEditing && sectionIds(editSections) !== sectionIds(savedEditSections);

  const registerSection = useMagneticSections({
    sectionKeys,
    activeKey: topicFilter,
    enabled: isMagnetic,
    scrollRequestId,
    scrollRootRef: canvasRef,
    onActiveChange: setTopicFilter,
    allKey: ALL_TOPICS_KEY,
  });

  const handleTopicChange = (topicId: string) => {
    setTopicFilter(topicId);
    if (topicId === ALL_TOPICS_KEY) {
      canvasRef.current?.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    // Bump even when the topic is already active so a re-click re-scrolls.
    setScrollRequestId(id => id + 1);
  };

  const exitEditing = () => setEditSections(null);

  const handleSectionReorder = (
    sectionKey: string,
    index: number,
    direction: ReorderDirection
  ) => {
    const section = (editSections || []).find(s => s.key === sectionKey);
    const movedId = section?.videos[index]?.id;
    runSmoothReorder(listReorderRefs.current[sectionKey], () => {
      setEditSections(prev =>
        (prev || []).map(group => {
          if (group.key !== sectionKey) return group;
          return {
            ...group,
            videos: moveItemInArray(group.videos, index, direction).map(
              (video, orderIndex) => ({
                ...video,
                sort_order: orderIndex + 1,
              })
            ),
          };
        })
      );
    });
    if (movedId) flash(movedId);
  };

  const handleSaveOrder = async () => {
    if (!editSections || !hasOrderChanges) return;
    setOrderSaving(true);
    try {
      const dirty = editSections.filter(section => {
        const saved = savedEditSections.find(s => s.key === section.key);
        if (!saved) return true;
        return (
          section.videos.map(v => v.id).join("|") !==
          saved.videos.map(v => v.id).join("|")
        );
      });
      for (let i = 0; i < dirty.length; i += 1) {
        const section = dirty[i];
        const isLast = i === dirty.length - 1;
        const result = await dispatch(
          reorderLibraryVideos({
            topic_id: section.topicId,
            library_video_ids: section.videos.map(video => video.id),
            // One toast + one library refresh on the final dirty topic.
            quiet: !isLast,
          })
        );
        if (!reorderLibraryVideos.fulfilled.match(result)) return;
      }
      exitEditing();
    } finally {
      setOrderSaving(false);
    }
  };

  const hasResults = isEditing
    ? editSections.some(section => section.videos.length > 0)
    : canvasSections.length > 0;

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    const result = await dispatch(removeLibraryVideo(deleteTarget.id));
    if (removeLibraryVideo.fulfilled.match(result)) setDeleteTarget(null);
  };

  const deleteRoleLabels = deleteTarget
    ? getRoleLabels(deleteTarget.assigned_roles)
    : [];

  const renderRoles = (video: LibraryVideo) => {
    const roleLabels = getRoleLabels(video.assigned_roles);
    return roleLabels.length > 0 ? (
      <TruncatedTooltip
        tooltip={`Assigned to: ${roleLabels.join(", ")}`}
        className="library-video-card__roles"
      >
        Assigned to {roleLabels.length}{" "}
        {roleLabels.length === 1 ? "role" : "roles"}
      </TruncatedTooltip>
    ) : (
      <span className="library-video-card__roles library-video-card__roles--none">
        Not assigned yet
      </span>
    );
  };

  const renderActions = (video: LibraryVideo) => (
    <ItemActionsMenu
      ariaLabel="Video actions"
      actions={[
        {
          id: "edit",
          label: "Edit",
          icon: Edit,
          onClick: () => setFormState({ open: true, video }),
        },
        {
          id: "delete",
          label: "Delete from library",
          icon: Trash2,
          danger: true,
          onClick: () => setDeleteTarget(video),
        },
      ]}
    />
  );

  const renderVideos = (
    videos: LibraryVideo[],
    showTopic: boolean,
    showOrder: boolean
  ) =>
    layout === "list" ? (
      <div className="library-video-list" role="list">
        {videos.map(video => {
          const thumbnailUrl = getYouTubeThumbnailUrl(video.video_url);
          return (
            <div key={video.id} className="library-video-row" role="listitem">
              <button
                type="button"
                className="library-video-row__thumb"
                onClick={() => setPreviewVideo(video)}
                title="Preview video"
              >
                {showOrder && (video.sort_order || 0) > 0 && (
                  <span className="library-video-card__order-badge">
                    {video.sort_order}
                  </span>
                )}
                {thumbnailUrl ? (
                  <img src={thumbnailUrl} alt={video.title || "Video"} />
                ) : (
                  <Play size={18} className="text-gray-300" />
                )}
              </button>
              <div className="library-video-row__main">
                <TruncatedTooltip
                  tooltip={video.title || "Untitled Video"}
                  className="library-video-card__title"
                >
                  {video.title || "Untitled Video"}
                </TruncatedTooltip>
                {video.description && (
                  <TruncatedTooltip
                    tooltip={video.description}
                    className="library-video-card__description"
                  >
                    {video.description}
                  </TruncatedTooltip>
                )}
              </div>
              {showTopic ? (
                <span className="library-video-row__topic">
                  {video.topic_label || "Uncategorized"}
                </span>
              ) : (
                <span />
              )}
              <div className="library-video-row__roles">
                {renderRoles(video)}
              </div>
              {renderActions(video)}
            </div>
          );
        })}
      </div>
    ) : (
      <div className="admin-video-container__grid library-video-grid">
        {videos.map(video => {
          const thumbnailUrl = getYouTubeThumbnailUrl(video.video_url);
          return (
            <article key={video.id} className="library-video-card">
              <button
                type="button"
                className="library-video-card__thumb"
                onClick={() => setPreviewVideo(video)}
                title="Preview video"
              >
                {showOrder && (video.sort_order || 0) > 0 && (
                  <span className="library-video-card__order-badge">
                    {video.sort_order}
                  </span>
                )}
                {thumbnailUrl ? (
                  <img src={thumbnailUrl} alt={video.title || "Video"} />
                ) : (
                  <Play size={28} className="text-gray-300" />
                )}
                {showTopic && (
                  <span className="library-video-card__topic">
                    {video.topic_label || "Uncategorized"}
                  </span>
                )}
              </button>
              <div className="library-video-card__body">
                <TruncatedTooltip
                  tooltip={video.title || "Untitled Video"}
                  className="library-video-card__title"
                >
                  {video.title || "Untitled Video"}
                </TruncatedTooltip>
                {video.description && (
                  <TruncatedTooltip
                    tooltip={video.description}
                    maxLines={2}
                    className="library-video-card__description"
                  >
                    {video.description}
                  </TruncatedTooltip>
                )}
              </div>
              <div className="library-video-card__footer">
                {renderRoles(video)}
                {renderActions(video)}
              </div>
            </article>
          );
        })}
      </div>
    );

  const renderCanvas = () => {
    if (loading && libraryVideos.length === 0) {
      return (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 border-t-purple-600" />
        </div>
      );
    }

    if (isEditing) {
      const isListEdit = layout === "list";
      return (
        <>
          <div className="reorder-banner" role="note">
            <span className="reorder-banner__icon">
              {isListEdit ? (
                <ArrowDownUp size={16} />
              ) : (
                <GripVertical size={16} />
              )}
            </span>
            <div>
              <p className="reorder-banner__title">
                {isListEdit
                  ? "Use move actions to set default order within each topic"
                  : "Drag cards to set default order within each topic"}
              </p>
              <p className="reorder-banner__text">
                This is the catalog default used when videos are newly assigned
                to a role. Existing role play orders are not changed. Click Save
                Order when you&apos;re done.{" "}
                <span className="reorder-banner__tip">
                  {isListEdit
                    ? "To drag and drop instead, discard, switch to card view, then click Edit Order."
                    : "To use move up/down controls instead, discard, switch to list view, then click Edit Order."}
                </span>
              </p>
            </div>
          </div>
          <div className="video-sections">
            {editSections.map(section => (
              <section
                key={section.key}
                ref={registerSection(section.key)}
                className={`video-section${
                  topicFilter === section.key ? " is-active" : ""
                }`}
              >
                <h4 className="video-section__hdr">
                  {section.label}
                  <span className="video-section__count">
                    {section.videos.length}
                  </span>
                </h4>
                {isListEdit ? (
                  <div
                    className="library-video-list"
                    role="list"
                    ref={node => {
                      listReorderRefs.current[section.key] = node;
                    }}
                  >
                    {section.videos.map((video, index) => {
                      const thumbnailUrl = getYouTubeThumbnailUrl(
                        video.video_url
                      );
                      return (
                        <div
                          key={video.id}
                          data-reorder-id={video.id}
                          className={`library-video-row library-video-row--reorder${
                            isHighlighted(video.id)
                              ? " is-reorder-highlight"
                              : ""
                          }`}
                          role="listitem"
                        >
                          <button
                            type="button"
                            className="library-video-row__thumb"
                            onClick={() => setPreviewVideo(video)}
                            title="Preview video"
                          >
                            <span className="library-video-card__order-badge">
                              {index + 1}
                            </span>
                            {thumbnailUrl ? (
                              <img
                                src={thumbnailUrl}
                                alt={video.title || "Video"}
                              />
                            ) : (
                              <Play size={18} className="text-gray-300" />
                            )}
                          </button>
                          <div className="library-video-row__main">
                            <TruncatedTooltip
                              tooltip={video.title || "Untitled Video"}
                              className="library-video-card__title"
                            >
                              {video.title || "Untitled Video"}
                            </TruncatedTooltip>
                            {video.description && (
                              <TruncatedTooltip
                                tooltip={video.description}
                                className="library-video-card__description"
                              >
                                {video.description}
                              </TruncatedTooltip>
                            )}
                          </div>
                          <div className="library-video-row__roles">
                            {renderRoles(video)}
                          </div>
                          <ReorderControls
                            index={index}
                            length={section.videos.length}
                            onMove={direction =>
                              handleSectionReorder(
                                section.key,
                                index,
                                direction
                              )
                            }
                          />
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <SortableLibraryGrid
                    videos={section.videos}
                    highlightId={highlightId}
                    onVideosChange={nextVideos =>
                      setEditSections(prev =>
                        (prev || []).map(group =>
                          group.key === section.key
                            ? { ...group, videos: nextVideos }
                            : group
                        )
                      )
                    }
                    onMoved={flash}
                    onPreview={setPreviewVideo}
                  />
                )}
              </section>
            ))}
          </div>
        </>
      );
    }

    if (!hasResults) {
      return (
        <EmptyState
          icon={libraryVideos.length === 0 ? Clapperboard : SearchX}
          title={
            libraryVideos.length === 0
              ? "Your video library is empty"
              : "No videos match these filters"
          }
          description={
            libraryVideos.length === 0
              ? "Add a video once here, then assign it to roles from the Role Assignments tab."
              : "Try a different topic or search term."
          }
        />
      );
    }

    return (
      <div className="video-sections">
        {canvasSections.map(section => (
          <section
            key={section.key}
            ref={registerSection(section.key)}
            className={`video-section${
              topicFilter === section.key ? " is-active" : ""
            }`}
          >
            <h4 className="video-section__hdr">
              {section.label}
              <span className="video-section__count">
                {section.videos.length}
              </span>
            </h4>
            {section.videos.length > 0 ? (
              renderVideos(section.videos, false, true)
            ) : (
              <p className="video-section__empty">
                No videos in this topic yet.
              </p>
            )}
          </section>
        ))}
      </div>
    );
  };

  return (
    <div className="admin-video-workspace__panel">
      {actionsSlot &&
        createPortal(
          <>
            <WorkspaceSearch
              value={search}
              onChange={setSearch}
              placeholder="Search library…"
              ariaLabel="Search video library"
              disabled={isEditing}
            />
            {isEditing ? (
              <>
                <Button
                  variant="secondary"
                  icon={<RotateCcw size={18} />}
                  onClick={exitEditing}
                >
                  Discard
                </Button>
                <Button
                  variant="primary"
                  icon={<Save size={18} />}
                  loading={orderSaving || saving}
                  disabled={!hasOrderChanges}
                  onClick={handleSaveOrder}
                >
                  Save Order
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="secondary"
                  disabled={libraryVideos.length < 2}
                  title="Edit default library order"
                  onClick={() => {
                    setSearch("");
                    // "All" is browse-only; Edit Order jumps to the first topic chip
                    // (e.g. How-to videos) so the filter bar matches sectioned reorder.
                    if (topicFilter === ALL_TOPICS_KEY) {
                      const firstTopicChip = filterTabs.find(
                        tab => tab.id !== ALL_TOPICS_KEY
                      );
                      if (firstTopicChip) setTopicFilter(firstTopicChip.id);
                    }
                    setEditSections(savedEditSections);
                    canvasRef.current?.scrollTo({ top: 0 });
                  }}
                  icon={<ArrowDownUp size={18} />}
                >
                  Edit Order
                </Button>
                <Button
                  variant="primary"
                  icon={<Plus size={18} />}
                  onClick={() => setFormState({ open: true, video: null })}
                >
                  Add Video
                </Button>
              </>
            )}
          </>,
          actionsSlot
        )}

      <div className="library-browse">
        <aside className="library-topic-nav" aria-label="Topics">
          <p className="library-topic-nav__title">Topics</p>
          <FilterChips
            chips={filterTabs}
            activeId={topicFilter}
            onChange={handleTopicChange}
            ariaLabel="Jump to topic videos"
            orientation="vertical"
          />
        </aside>

        <div className="library-browse__main">
          <div className="library-browse__toolbar">
            <LayoutToggle
              value={layout}
              onChange={setLayout}
              disabled={isEditing}
            />
          </div>
          <div className="admin-video-workspace__canvas" ref={canvasRef}>
            {renderCanvas()}
          </div>
        </div>
      </div>

      <LibraryVideoFormModal
        isOpen={formState.open}
        video={formState.video}
        onClose={() => setFormState({ open: false, video: null })}
        onEditExisting={video => setFormState({ open: true, video })}
      />

      <VideoPreviewModal
        video={previewVideo}
        onClose={() => setPreviewVideo(null)}
      />

      <DeleteModal
        isOpen={!!deleteTarget}
        toggle={() => setDeleteTarget(null)}
        title="Delete Library Video"
        entityName={
          deleteTarget?.title ? `"${deleteTarget.title}"` : "this video"
        }
        warningItems={[
          deleteRoleLabels.length > 0
            ? `It will be removed from: ${deleteRoleLabels.join(", ")}.`
            : "It is not assigned to any role.",
          "This action cannot be undone.",
        ]}
        onConfirm={handleConfirmDelete}
        loading={saving}
      />
    </div>
  );
};

export default LibraryPanel;
